import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Grid2,
  Modal,
  IconButton,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Checkbox,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import { CacheProvider } from "@emotion/react";
import { prefixer } from "stylis";
import rtlPlugin from "stylis-plugin-rtl";
import createCache from "@emotion/cache";
import CloseIcon from "@mui/icons-material/Close";
import axiosInstance from "../../../../api/axiosConfig";

const cacheRtl = createCache({
  key: "adapter-date-fns-jalali-demo",
  stylisPlugins: [prefixer, rtlPlugin],
});

function AddSessionModal({
  open,
  onClose,
  scheduleId,
  campId,
  onSessionAdded,
}) {
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [playerList, setPlayerList] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [loadingPlayers, setLoadingPlayers] = useState(true);
  const [loadingActivityTypes, setLoadingActivityTypes] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    description: "",
    startTime: "",
    endTime: "",
    pressureLevel: 0,
    baseActivityTypeId: "",
  });

  // Fetch players from backend
  useEffect(() => {
    if (open) {
      const fetchPlayers = async () => {
        try {
          setLoadingPlayers(true);
          const response = await axiosInstance.get(
            `/Training/api/Training/GetTrainingCampUsers?CampId=${campId}`,
          );
          setPlayerList(
            response.data.map((player) => ({
              id: player.id,
              name: `${player.fName} ${player.lName}`,
            })),
          );
        } catch (err) {
          if (err.response.data.message) {
            setError(`خطا: ${err.response.data.message}`);
          } else {
            setError("خطا در دریافت لیست بازیکنان.");
          }
        } finally {
          setLoadingPlayers(false);
        }
      };

      const fetchActivityTypes = async () => {
        try {
          setLoadingActivityTypes(true);
          const response = await axiosInstance.get(
            `/Base/api/Base/GetBaseActivityList`,
          );
          setActivityTypes(response.data.data.dataList);
        } catch (err) {
          if (err.response.data.message) {
            setError(`خطا: ${err.response.data.message}`);
          } else {
            setError("خطا در دریافت لیست انواع فعالیت.");
          }
        } finally {
          setLoadingActivityTypes(false);
        }
      };

      fetchPlayers();
      fetchActivityTypes();
    }
  }, [open, campId]);

  const handlePlayerChange = (event) => {
    const {
      target: { value },
    } = event;

    if (value.includes("all")) {
      if (selectedPlayers.length === playerList.length) {
        setSelectedPlayers([]);
      } else {
        setSelectedPlayers(playerList.map((player) => player.id));
      }
    } else {
      setSelectedPlayers(typeof value === "string" ? value.split(",") : value);
    }
  };

  const isAllSelected = selectedPlayers.length === playerList.length;

  const handleChange = (field) => (event) => {
    const { value } = event.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    if (
      !formData.description ||
      !formData.startTime ||
      !formData.endTime ||
      !formData.baseActivityTypeId
    ) {
      setError("لطفاً تمام فیلدهای مورد نیاز را پر کنید.");
      return;
    }

    if (selectedPlayers.length === 0) {
      setError("لطفاً حداقل یک بازیکن را انتخاب کنید.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await axiosInstance.post(`/Training/api/Training/CreateTrainingSession`, {
        trainingScheduleId: scheduleId,
        baseActivityTypeId: formData.baseActivityTypeId,
        description: formData.description,
        exerciseStartTime: formData.startTime,
        exerciseEndTime: formData.endTime,
        pressureLevel: formData.pressureLevel,
        users: selectedPlayers,
      });
      onSessionAdded(); // Notify parent component
      onClose(); // Close modal
    } catch (err) {
      if (err.response.data.message) {
        setError(`خطا: ${err.response.data.message}`);
      } else {
        setError("خطا در ذخیره جلسه تمرینی.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="add-session-modal">
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: "800px",
          bgcolor: "background.paper",
          border: "1px solid #ccc",
          borderRadius: "8px",
          boxShadow: 24,
          p: 4,
          direction: "rtl",
        }}
      >
        <Grid2
          container
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography id="add-session-modal" variant="h6">
            افزودن فعالیت جدید در روز
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Grid2>

        {loadingPlayers || loadingActivityTypes ? (
          <CircularProgress
            sx={{ display: "block", margin: "0 auto", mt: 4 }}
          />
        ) : (
          <>
            {error && (
              <Typography color="error" sx={{ mb: 2 }}>
                {error}
              </Typography>
            )}

            <Grid2 container spacing={2}>
              <Grid2 size={12}>
                <CacheProvider value={cacheRtl}>
                  <div dir="rtl">
                    <FormControl fullWidth>
                      <InputLabel id="activity-type-label">
                        نوع فعالیت
                      </InputLabel>
                      <Select
                        labelId="activity-type-label"
                        value={formData.baseActivityTypeId}
                        onChange={handleChange("baseActivityTypeId")}
                        label="نوع فعالیت"
                        dir="rtl"
                      >
                        {activityTypes.map((type) => (
                          <MenuItem key={type.id} value={type.id} dir="rtl">
                            {type.title}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                </CacheProvider>
              </Grid2>
              <Grid2 size={6}>
                <CacheProvider value={cacheRtl}>
                  <div dir="rtl">
                    <TextField
                      fullWidth
                      label="عنوان فعالیت"
                      placeholder="مثلاً دویدن"
                      value={formData.description}
                      onChange={handleChange("description")}
                    />
                  </div>
                </CacheProvider>
              </Grid2>
              <Grid2 size={6}>
                <CacheProvider value={cacheRtl}>
                  <div dir="rtl">
                    <TextField
                      fullWidth
                      type="number"
                      label="سطح فشار"
                      value={formData.pressureLevel}
                      onChange={handleChange("pressureLevel")}
                      style={{
                        display:
                          formData.baseActivityTypeId === 1 ? "block" : "none",
                      }}
                    />
                  </div>
                </CacheProvider>
              </Grid2>
              <Grid2 size={6}>
                <CacheProvider value={cacheRtl}>
                  <div dir="rtl">
                    <TextField
                      fullWidth
                      type="time"
                      label="ساعت شروع"
                      InputLabelProps={{ shrink: true }}
                      value={formData.startTime}
                      onChange={handleChange("startTime")}
                    />
                  </div>
                </CacheProvider>
              </Grid2>
              <Grid2 size={6}>
                <CacheProvider value={cacheRtl}>
                  <div dir="rtl">
                    <TextField
                      fullWidth
                      type="time"
                      label="ساعت پایان"
                      InputLabelProps={{ shrink: true }}
                      value={formData.endTime}
                      onChange={handleChange("endTime")}
                    />
                  </div>
                </CacheProvider>
              </Grid2>
            </Grid2>

            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "bold", mb: 2, mt: 4 }}
            >
              بازیکنان شرکت کننده
            </Typography>
            <CacheProvider value={cacheRtl}>
              <div dir="rtl">
                <FormControl fullWidth>
                  <InputLabel id="player-select-label">
                    انتخاب بازیکنان
                  </InputLabel>
                  <Select
                    labelId="player-select-label"
                    multiple
                    label="انتخاب بازیکنان"
                    value={selectedPlayers}
                    onChange={handlePlayerChange}
                    renderValue={(selected) =>
                      selected.length === playerList.length
                        ? "همه بازیکنان انتخاب شده‌اند"
                        : selected
                            .map(
                              (id) => playerList.find((p) => p.id === id)?.name,
                            )
                            .join(", ")
                    }
                  >
                    <MenuItem value="all" dir="rtl">
                      <Checkbox checked={isAllSelected} />
                      <ListItemText primary="انتخاب همه" />
                    </MenuItem>
                    {playerList.map((player) => (
                      <MenuItem key={player.id} value={player.id} dir="rtl">
                        <Checkbox
                          checked={selectedPlayers.indexOf(player.id) > -1}
                          dir="rtl"
                        />
                        <ListItemText primary={player.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </CacheProvider>

            <Box
              sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}
            >
              <Button variant="outlined" onClick={onClose}>
                انصراف
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? "در حال ذخیره..." : "ذخیره"}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
}

export default AddSessionModal;
